import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, RefreshCw, Wand2, Rocket, FlaskConical, Archive, FileText, CheckCircle2, AlertTriangle, Send } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: { label: 'Borrador', cls: 'border-amber-400/30 bg-amber-500/10 text-amber-200' },
  published: { label: 'Publicado', cls: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' },
  published_simulated: { label: 'Publicado (simulado)', cls: 'border-sky-400/30 bg-sky-500/10 text-sky-200' },
  archived: { label: 'Archivado', cls: 'border-border-subtle bg-background/50 text-text-muted' },
};

const Badge = ({ status }) => {
  const b = STATUS_BADGE[status] || STATUS_BADGE.draft;
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] ${b.cls}`}>{b.label}</span>;
};

export const ServicesStudioPanel = ({ authFetch }) => {
  const [status, setStatus] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState('cálido y profesional');
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [testMsg, setTestMsg] = useState('Hola, ¿qué servicios ofrecen?');
  const [testReply, setTestReply] = useState(null);
  const [busy, setBusy] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = useCallback(async (notify = false) => {
    try {
      setLoading(true);
      const [s, l, rq] = await Promise.all([authFetch(`${API}/studio/status`), authFetch(`${API}/studio/services`), authFetch(`${API}/studio/requests`)]);
      const sj = await s.json(), lj = await l.json(), rqj = await rq.json();
      setStatus(sj); setServices(lj.services || []); setRequests(rqj.requests || []);
      if (notify) toast.success('Estudio actualizado');
    } catch (e) { toast.error('No se pudo cargar el Estudio'); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const activateRequest = async (requestId) => {
    try {
      setBusy(requestId);
      const r = await authFetch(`${API}/studio/requests/${requestId}/activate`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'No se pudo activar');
      toast.success('Agente activado para el cliente');
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const openDetail = async (serviceId) => {
    try {
      setBusy(serviceId); setTestReply(null);
      const r = await authFetch(`${API}/studio/services/${serviceId}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'No encontrado');
      setSelected(j);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const generate = async () => {
    if (brief.trim().length < 10) { toast.error('Describe el agente con al menos 10 caracteres'); return; }
    try {
      setGenerating(true);
      const r = await authFetch(`${API}/studio/services/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief: brief.trim(), tone }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error al generar');
      toast.success(j.simulated ? 'Borrador generado (heurístico, flag OFF)' : 'Borrador generado con IA');
      setBrief('');
      await load();
      openDetail(j.service_id);
    } catch (e) { toast.error(e.message); } finally { setGenerating(false); }
  };

  const runTest = async () => {
    if (!selected) return;
    try {
      setBusy('test');
      const r = await authFetch(`${API}/studio/services/${selected.service_id}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: testMsg, sample_vars: {} }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error en prueba');
      setTestReply(j);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const publish = async (serviceId) => {
    try {
      setBusy(serviceId);
      const r = await authFetch(`${API}/studio/services/${serviceId}/publish`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'object' ? (j.detail.errors || []).join(', ') : (j.detail || 'No se pudo publicar'));
      toast.success(j.simulated ? 'Publicado en simulado (flag services_studio OFF)' : `Publicado como plantilla ${j.template_id}`);
      await load(); openDetail(serviceId);
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const archive = async (serviceId) => {
    try {
      setBusy(serviceId);
      const r = await authFetch(`${API}/studio/services/${serviceId}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('No se pudo archivar');
      toast.success('Servicio archivado');
      setSelected(null); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const st = status?.stats || {};

  return (
    <section className="space-y-6" data-testid="services-studio-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">Fase 9 · Estudio de servicios de IA</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Estudio · Crea y publica agentes</h3>
          <p className="mt-1 text-xs text-text-muted">Describe un agente, genera prompt + wizard con IA, pruébalo y publícalo como plantilla instalable.</p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading} data-testid="studio-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Servicios', value: st.services_total ?? 0, helper: 'en el estudio', id: 'studio-stat-total' },
          { label: 'Borradores', value: st.drafts ?? 0, helper: 'sin publicar', id: 'studio-stat-drafts' },
          { label: 'Publicados', value: st.published ?? 0, helper: 'plantillas activas', id: 'studio-stat-published' },
          { label: 'Pruebas', value: st.test_runs ?? 0, helper: 'ejecutadas', id: 'studio-stat-tests' },
        ].map((c) => (
          <div key={c.id} className="glass-card p-5" data-testid={c.id}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{c.label}</p>
            <p className="mt-3 text-3xl font-bold text-text-heading">{c.value}</p>
            <p className="mt-2 text-xs text-text-muted">{c.helper}</p>
          </div>
        ))}
      </div>

      {/* Generador desde brief */}
      <div className="glass-card p-5" data-testid="studio-generator">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Wand2 className="h-4 w-4 text-primary" /> Generar agente desde un brief</h4>
        <textarea data-testid="studio-brief-input" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
          placeholder="Ej: agente para veterinaria que agenda citas y recuerda vacunas a los dueños"
          className="w-full rounded-xl border border-border-subtle bg-background/70 p-3 text-sm text-text-heading placeholder:text-text-muted/60 focus:border-primary focus:outline-none" />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input data-testid="studio-tone-input" value={tone} onChange={(e) => setTone(e.target.value)}
            className="flex-1 min-w-[180px] rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none" placeholder="Tono" />
          <button type="button" onClick={generate} disabled={generating} data-testid="studio-generate-btn"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-semibold text-background transition hover:opacity-90 disabled:opacity-50">
            {generating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generar borrador
          </button>
        </div>
      </div>

      {/* Bandeja de solicitudes del catálogo público */}
      <div className="glass-card p-5" data-testid="studio-requests-inbox">
        <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-text-heading"><FileText className="h-4 w-4 text-primary" /> Solicitudes del catálogo <span className="rounded-full border border-border-subtle px-2 py-0.5 text-[10px] text-text-muted">{requests.length}</span></h4>
        <div className="space-y-2" data-testid="studio-requests-list">
          {requests.map((rq) => (
            <div key={rq.request_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-subtle bg-background/50 p-3" data-testid={`studio-request-${rq.request_id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text-heading">{rq.agent_name} · {rq.contact_name || rq.client_id}</p>
                <p className="truncate text-xs text-text-muted">{rq.client_id} · {rq.contact_wa || 's/WA'} · {new Date(rq.created_at).toLocaleString('es-PE')}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] ${rq.status === 'activated' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : rq.status === 'install_started' ? 'border-sky-400/30 bg-sky-500/10 text-sky-200' : 'border-amber-400/30 bg-amber-500/10 text-amber-200'}`}>{rq.status}</span>
                {rq.status !== 'activated' && (
                  <button type="button" disabled={busy === rq.request_id} onClick={() => activateRequest(rq.request_id)} data-testid={`studio-request-activate-${rq.request_id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-[11px] font-semibold text-background transition hover:opacity-90 disabled:opacity-50">
                    <Rocket className="h-3.5 w-3.5" /> Activar
                  </button>
                )}
              </div>
            </div>
          ))}
          {!requests.length && <p className="text-sm text-text-muted" data-testid="studio-requests-empty">Sin solicitudes del catálogo todavía.</p>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Lista de servicios */}
        <div className="glass-card p-5" data-testid="studio-services-card">
          <h4 className="mb-4 text-sm font-semibold text-text-heading">Servicios del estudio</h4>
          <div className="space-y-3" data-testid="studio-services-list">
            {services.map((s) => (
              <button key={s.service_id} type="button" onClick={() => openDetail(s.service_id)} data-testid={`studio-service-${s.service_id}`}
                className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left transition hover:border-primary/40 ${selected?.service_id === s.service_id ? 'border-primary/50 bg-primary/5' : 'border-border-subtle bg-background/50'}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text-heading">{s.name}</p>
                  <p className="truncate text-xs text-text-muted">{s.sector} · v{s.version} · {s.description?.slice(0, 60)}</p>
                </div>
                <Badge status={s.status} />
              </button>
            ))}
            {!services.length && <p className="text-sm text-text-muted" data-testid="studio-services-empty">Aún no hay servicios. Genera uno desde un brief arriba.</p>}
          </div>
        </div>

        {/* Detalle / prueba / publicar */}
        <div className="glass-card p-5" data-testid="studio-detail-card">
          {!selected && <p className="text-sm text-text-muted" data-testid="studio-detail-empty">Selecciona un servicio para ver su prompt, probarlo y publicarlo.</p>}
          {selected && (
            <div className="space-y-4" data-testid={`studio-detail-${selected.service_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-base font-semibold text-text-heading">{selected.name}</h4>
                  <p className="mt-0.5 text-xs text-text-muted">{selected.sector} · v{selected.version} <Badge status={selected.status} /></p>
                </div>
              </div>

              {selected.validation_errors?.length > 0 ? (
                <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3" data-testid="studio-validation-errors">
                  <p className="flex items-center gap-2 text-xs font-semibold text-amber-200"><AlertTriangle className="h-4 w-4" /> No listo para publicar</p>
                  <ul className="mt-2 list-disc pl-5 text-[11px] text-amber-100/80">
                    {selected.validation_errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              ) : (
                <p className="flex items-center gap-2 text-xs text-emerald-300" data-testid="studio-validation-ok"><CheckCircle2 className="h-4 w-4" /> Validado · listo para publicar</p>
              )}

              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-text-muted"><FileText className="h-3.5 w-3.5" /> Prompt del sistema (preview)</p>
                <pre className="max-h-40 overflow-auto rounded-xl border border-border-subtle bg-background/70 p-3 text-[11.5px] leading-relaxed text-text-muted" data-testid="studio-prompt-preview">{selected.preview?.system_prompt || selected.system_prompt_template}</pre>
              </div>

              {/* Probar */}
              <div className="rounded-xl border border-border-subtle bg-background/50 p-3">
                <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-text-muted"><FlaskConical className="h-3.5 w-3.5" /> Probar agente</p>
                <div className="flex gap-2">
                  <input data-testid="studio-test-input" value={testMsg} onChange={(e) => setTestMsg(e.target.value)}
                    className="flex-1 rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none" placeholder="Mensaje de prueba" />
                  <button type="button" onClick={runTest} disabled={busy === 'test'} data-testid="studio-test-btn"
                    className="inline-flex items-center gap-1 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading disabled:opacity-50">
                    <Send className="h-3.5 w-3.5" /> Enviar
                  </button>
                </div>
                {testReply && (
                  <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-text-heading" data-testid="studio-test-reply">
                    <p className="mb-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">Respuesta ({testReply.source})</p>
                    {testReply.reply}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button type="button" onClick={() => publish(selected.service_id)} disabled={busy === selected.service_id || selected.validation_errors?.length > 0}
                  data-testid="studio-publish-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-xs font-semibold text-background transition hover:opacity-90 disabled:opacity-40">
                  <Rocket className="h-4 w-4" /> Publicar como plantilla
                </button>
                <button type="button" onClick={() => archive(selected.service_id)} disabled={busy === selected.service_id}
                  data-testid="studio-archive-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-rose-400/30 px-4 py-2.5 text-xs font-semibold text-rose-200 transition hover:bg-rose-500/10 disabled:opacity-50">
                  <Archive className="h-4 w-4" /> Archivar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesStudioPanel;
